import { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Search, Filter, Trash2, Grid, CheckCircle2, Plus } from 'lucide-react';
import { imageService } from '../../services/imageService';
import type { ImageMetadata, ImageFolder } from '../../types/images';
import ImageUpload from './ImageUpload';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import EmptyState from '../../components/EmptyState';
import InlineErrorDisplay from '../../components/InlineErrorDisplay';
import Button from '../../components/Button';

interface ImageManagerProps {
    isOpen: boolean;
    onClose: () => void;
    onSelectImage: (url: string) => void;
    allowedFolders?: ImageFolder[];
    currentFolder?: ImageFolder;
    title?: string;
}

export default function ImageManager({
    isOpen,
    onClose,
    onSelectImage,
    allowedFolders,
    currentFolder,
    title = "Image Library"
}: ImageManagerProps) {
    const [images, setImages] = useState<ImageMetadata[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFolder, setSelectedFolder] = useState<ImageFolder | 'all'>(currentFolder || 'all');
    const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
    const [showUpload, setShowUpload] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const allFolders: ImageFolder[] = ['sections', 'lessons', 'content', 'general', 'audio-guides', 'journey-chapters'];
    const availableFolders = allowedFolders || allFolders;

    const loadImages = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            let imageData: ImageMetadata[];

            if (selectedFolder === 'all') {
                imageData = await imageService.listAllImages();
            } else {
                imageData = await imageService.listImages(selectedFolder);
            }

            // Filter by allowed folders if specified
            if (allowedFolders) {
                imageData = imageData.filter(img => allowedFolders.includes(img.folder as ImageFolder));
            }

            setImages(imageData);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load images');
        } finally {
            setLoading(false);
        }
    }, [selectedFolder, allowedFolders]);

    // Load images on mount and when folder changes
    useEffect(() => {
        if (isOpen) {
            loadImages();
        }
    }, [isOpen, loadImages]);

    // Filter images based on search term
    const filteredImages = useMemo(() => {
        return images.filter(image =>
            image.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [images, searchTerm]);

    const handleImageClick = (image: ImageMetadata) => {
        if (selectedImages.size > 0) {
            // Selection mode - toggle selection
            const newSelected = new Set(selectedImages);
            if (newSelected.has(image.url)) {
                newSelected.delete(image.url);
            } else {
                newSelected.add(image.url);
            }
            setSelectedImages(newSelected);
        } else {
            // Normal mode - select image
            onSelectImage(image.url);
            onClose();
        }
    };

    const handleImageLongPress = (image: ImageMetadata) => {
        // Start selection mode
        setSelectedImages(new Set([image.url]));
    };

    const handleSelectAll = () => {
        if (selectedImages.size === filteredImages.length) {
            setSelectedImages(new Set());
        } else {
            setSelectedImages(new Set(filteredImages.map(img => img.url)));
        }
    };

    const handleDeleteSelected = async () => {
        if (selectedImages.size === 0) return;

        try {
            setLoading(true);

            // Delete all selected images
            await Promise.all(
                Array.from(selectedImages).map(url => imageService.deleteImage(url))
            );

            setSelectedImages(new Set());
            setShowDeleteConfirm(false);
            await loadImages(); // Refresh the list
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete images');
        } finally {
            setLoading(false);
        }
    };

    const handleUploadSuccess = () => {
        setShowUpload(false);
        loadImages(); // Refresh the list
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600/50 overflow-y-auto h-full w-full z-[60] p-4">
            <div className="relative mx-auto p-0 border w-full max-w-6xl min-h-[80vh] shadow-lg rounded-md bg-white">
                {/* Header */}
                <div className="flex flex-wrap items-center justify-between gap-2 p-4 sm:p-6 border-b border-gray-200">
                    <div className="flex flex-wrap items-center gap-4 min-w-0">
                        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
                        {selectedImages.size > 0 && (
                            <span className="text-sm text-blue-600 bg-blue-100 px-2 py-1 rounded">
                                {selectedImages.size} selected
                            </span>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        {selectedImages.size > 0 && (
                            <>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleSelectAll}
                                >
                                    {selectedImages.size === filteredImages.length ? 'Deselect All' : 'Select All'}
                                </Button>
                                <Button
                                    variant="danger"
                                    size="sm"
                                    icon={<Trash2 size={20} />}
                                    onClick={() => setShowDeleteConfirm(true)}
                                />
                            </>
                        )}

                        <Button
                            variant="primary"
                            size="md"
                            icon={<Plus size={16} />}
                            iconPosition="left"
                            onClick={() => setShowUpload(!showUpload)}
                        >
                            Upload
                        </Button>

                        <Button
                            variant="ghost"
                            size="sm"
                            icon={<X size={24} />}
                            onClick={onClose}
                        />
                    </div>
                </div>

                {/* Upload Section */}
                {showUpload && (
                    <div className="p-4 sm:p-6 border-b border-gray-200 bg-gray-50">
                        <ImageUpload
                            folder={(selectedFolder === 'all' ? currentFolder : selectedFolder) || 'general'}
                            onUploadSuccess={handleUploadSuccess}
                            onUploadError={(error) => setError(error)}
                        />
                    </div>
                )}

                {/* Controls */}
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                        {/* Search */}
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                            <input
                                type="text"
                                placeholder="Search images..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        {/* Folder Filter */}
                        <div className="flex items-center gap-2">
                            <Filter size={16} className="text-gray-400" />
                            <select
                                value={selectedFolder}
                                onChange={(e) => setSelectedFolder(e.target.value as ImageFolder | 'all')}
                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="all">All Folders</option>
                                {availableFolders.map(folder => (
                                    <option key={folder} value={folder}>
                                        {folder.charAt(0).toUpperCase() + folder.slice(1)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6">
                    {loading ? (
                        <div className="flex justify-center py-12">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                                <p className="mt-4 text-gray-600">Loading images...</p>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="py-6">
                            <InlineErrorDisplay
                                message={`Error loading images: ${error}`}
                                onDismiss={() => setError(null)}
                            />
                            <div className="text-center mt-4">
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={loadImages}
                                >
                                    Retry
                                </Button>
                            </div>
                        </div>
                    ) : filteredImages.length === 0 ? (
                        <EmptyState
                            icon={<Grid className="mx-auto h-12 w-12 text-gray-400" />}
                            title="No images found"
                            description={searchTerm ? 'Try adjusting your search terms' : 'Upload some images to get started'}
                            action={!searchTerm ? {
                                label: "Upload Images",
                                onClick: () => setShowUpload(true),
                                variant: "primary"
                            } : undefined}
                        />
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                            {filteredImages.map((image) => (
                                <div
                                    key={image.url}
                                    className={`relative group cursor-pointer border-2 rounded-lg overflow-hidden transition-all duration-200 hover:shadow-md
                    ${selectedImages.has(image.url)
                                            ? 'border-blue-500 ring-2 ring-blue-500 ring-opacity-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                    onClick={() => handleImageClick(image)}
                                    onContextMenu={(e) => {
                                        e.preventDefault();
                                        handleImageLongPress(image);
                                    }}
                                >
                                    {/* Selection Indicator */}
                                    {selectedImages.has(image.url) && (
                                        <div className="absolute top-2 right-2 z-10">
                                            <CheckCircle2 className="w-5 h-5 text-blue-600 bg-white rounded-full" />
                                        </div>
                                    )}

                                    {/* Image */}
                                    <div className="aspect-square bg-gray-100 flex items-center justify-center">
                                        <img
                                            src={image.url}
                                            alt={image.name}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    </div>

                                    {/* Info Overlay */}
                                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white p-2 transform translate-y-full group-hover:translate-y-0 transition-transform duration-200">
                                        <p className="text-xs font-medium truncate">{image.name}</p>
                                        <p className="text-xs text-gray-300">
                                            {formatFileSize(image.size)} • {image.folder}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Dialog */}
            <ConfirmationDialog
                isOpen={showDeleteConfirm}
                title="Delete Images"
                message={`Are you sure you want to delete ${selectedImages.size} image${selectedImages.size > 1 ? 's' : ''}? This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                confirmVariant="danger"
                onConfirm={handleDeleteSelected}
                onCancel={() => setShowDeleteConfirm(false)}
            />
        </div>
    );
} 